// views/auth/SignIn.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.jpg";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

// IMPORT FIREBASE SERVICES
import { signInService, checkAdminEmail } from "../../../Service/AuthService";
import { useAuth } from "../../../contexts/AuthContext";

function SignIn() {
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    console.log("Current User in SignIn:", currentUser);
    if (currentUser) {
      navigate("/admin");
    }
  }, [currentUser, navigate]);

  // Function to convert Firebase error codes to user-friendly messages
  const getErrorMessage = (errorCode) => {
    const errorMessages = {
      "auth/invalid-email": "Invalid email or password. Please check your credentials.",
      "auth/user-disabled": "This account has been disabled. Please contact support.",
      "auth/user-not-found": "No account found with this email. Please check your email or sign up.",
      "auth/wrong-password": "Invalid email or password. Please check your credentials.",
      "auth/invalid-credential": "Invalid email or password. Please check your credentials.",
      "auth/too-many-requests": "Too many failed login attempts. Please try again later or reset your password.",
      "auth/network-request-failed": "Network error. Please check your internet connection.",
      "auth/email-already-in-use": "This email is already registered. Please sign in instead.",
      "auth/weak-password": "Password is too weak. Please use at least 8 characters.",
      "auth/operation-not-allowed": "Sign in is currently disabled. Please contact support.",
      "auth/account-exists-with-different-credential": "An account already exists with this email using a different sign-in method.",
      "auth/invalid-verification-code": "Invalid verification code. Please try again.",
      "auth/invalid-verification-id": "Verification session expired. Please try again.",
      "auth/missing-password": "Please enter your password.",
      "auth/internal-error": "An unexpected error occurred. Please try again.",
    };

    return errorMessages[errorCode] || "An error occurred during sign in. Please try again.";
  };

  const handleSignIn = async () => {
    if (!email || !password) {
      toast({
        title: "Missing Information",
        description: "Please enter both email and password to continue.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Invalid email or password. Please check your credentials.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    
    // Check if email exists in admin collection
    try {
      const isAdminEmail = await checkAdminEmail(email);
      
      if (!isAdminEmail) {
        setLoading(false);
        toast({
          title: "Access Denied",
          description: "Only authorized admin emails can access this system.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
    } catch (error) {
      setLoading(false);
      toast({
        title: "Verification Error",
        description: "Unable to verify email permissions. Please try again later.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    // Proceed with sign-in if email exists in admin collection
    const res = await signInService(email, password);
    setLoading(false);

    if (res.success) {
      toast({
        title: "Welcome Back!",
        description: "You've successfully signed in.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      navigate("/admin");
    } else {
      // Extract error code from Firebase error string
      let errorCode = res.error;

      // If error is in format "Firebase: Error (auth/error-code)." extract the code
      const match = res.error.match(/\(([^)]+)\)/);
      if (match) {
        errorCode = match[1];
      }

      const userFriendlyMessage = getErrorMessage(errorCode);

      toast({
        title: "Sign In Failed",
        description: userFriendlyMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSignIn();
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "0px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Sign In
          </Heading>
          <Text mb="36px" ms="4px" color={textColorSecondary}>
            Enter your email and password to sign in!
          </Text>
        </Box>

        <Flex
          direction="column"
          w={{ base: "100%", md: "420px" }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
        >
          {/* Email */}
          <FormControl mb="15px">
            <FormLabel color={textColor} fontSize="sm" fontWeight="500">
              Email<Text as="span" color={brandStars}>*</Text>
            </FormLabel>
            <Input
              type="email"
              variant="auth"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              size="lg"
            />
          </FormControl>

          {/* Password */}
          <FormControl mb="20px">
            <FormLabel color={textColor} fontSize="sm" fontWeight="500">
              Password<Text as="span" color={brandStars}>*</Text>
            </FormLabel>

            <InputGroup size="md">
              <Input
                type={show ? "text" : "password"}
                placeholder="Min. 8 characters"
                variant="auth"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                size="lg"
              />
              <InputRightElement>
                <Icon
                  color={textColorSecondary}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={() => setShow(!show)}
                  cursor="pointer"
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Flex justifyContent="space-between" align="center" mb="24px">
            <Checkbox colorScheme="brandScheme">Keep me logged in</Checkbox>

            <NavLink to="/auth/forgot-password">
              <Text color={textColorBrand} fontSize="sm" fontWeight="500">
                Forgot password?
              </Text>
            </NavLink>
          </Flex>

          {/* SIGN-IN BUTTON */}
          <Button
            fontSize="sm"
            variant="brand"
            w="100%"
            h="50px"
            mb="24px"
            isLoading={loading}
            onClick={handleSignIn}
          >
            Sign In
          </Button>

          <Text color={textColorDetails} fontSize="14px">
            Not registered yet?
            <NavLink to="/auth/sign-up">
              <Text as="span" color={textColorBrand} ms="5px" fontWeight="500">
                Create an Account
              </Text>
            </NavLink>
          </Text>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;